/**
 * خدمة مزامنة البيانات بين التبويبات
 * تستخدم BroadcastChannel API للتواصل الآمن بين التبويبات المختلفة
 * نمط Singleton للحفاظ على استمرارية القناة طوال مدة فتح التبويب
 */

// إنشاء قناة البث بمعرف محدد للتطبيق
const AUTH_CHANNEL = 'dose_auth_channel';

// متغير عام لتخزين القناة (Singleton)
let broadcastChannel;

// تعريف معرف فريد للتبويب الحالي
const CURRENT_TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

// متغير لتخزين دالة المعالجة الحالية
let currentMessageHandler = null;

// علم للإشارة إلى أن القناة قيد الاستخدام
let isChannelActive = false;

// التأكد من دعم المتصفح للـ BroadcastChannel API
const isBroadcastSupported = () => {
  return typeof BroadcastChannel !== 'undefined';
};

// إعداد مستمع لإغلاق النافذة لتنظيف القناة
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // تنظيف قناة البث عند إغلاق التبويب/النافذة
    if (broadcastChannel) {
      broadcastChannel.close();
      broadcastChannel = null;
      isChannelActive = false;
    }
  });
}

/**
 * تهيئة قناة البث وإعداد الأحداث (Singleton)
 * @param {Function} onMessageCallback دالة تُستدعى عند استقبال رسالة جديدة
 */
export const initAuthChannel = (onMessageCallback) => {
  // محاولة تهيئة قناة البث
  
  if (!isBroadcastSupported()) {
    // المتصفح لا يدعم BroadcastChannel API
    return false;
  }

  try {
    // حفظ معالج الرسائل الجديد
    if (onMessageCallback && typeof onMessageCallback === 'function') {
      currentMessageHandler = onMessageCallback;
    }

    // إنشاء القناة إذا لم تكن موجودة بالفعل (نمط Singleton)
    if (!broadcastChannel) {
      broadcastChannel = new BroadcastChannel(AUTH_CHANNEL);
      
      // إعداد مستمع للرسائل الواردة
      broadcastChannel.onmessage = (event) => {
        // استخدام معالج الرسائل المخزن حالياً
        if (currentMessageHandler) {
          currentMessageHandler(event.data);
        }
      };
      
      isChannelActive = true;
    } else {
      isChannelActive = true;
    }
    return true;
  } catch (error) {
    // فشل في تهيئة قناة المزامنة
    return false;
  }
};

/**
 * إرسال بيانات المصادقة إلى جميع التبويبات المفتوحة
 * @param {Object} authData بيانات المصادقة المراد مشاركتها
 */
export const broadcastAuthData = (authData) => {
  // محاولة بث بيانات المصادقة
  
  if (!broadcastChannel || !isBroadcastSupported()) {
    // قناة البث غير متاحة لإرسال بيانات المصادقة
    return false;
  }

  try {
    // إضافة طابع وقت للرسالة
    const message = {
      type: 'AUTH_SYNC',
      data: authData,
      sourceTabId: CURRENT_TAB_ID,
      timestamp: Date.now()
    };
    
    // بث الرسالة لجميع التبويبات المفتوحة
    broadcastChannel.postMessage(message);
    return true;
  } catch (error) {
    // فشل في بث بيانات المصادقة
    return false;
  }
};

/**
 * طلب بيانات المصادقة من التبويبات الأخرى
 */
export const requestAuthData = () => {
  if (!broadcastChannel || !isBroadcastSupported()) {
    // قناة البث غير متاحة لطلب بيانات المصادقة
    return false;
  }
  
  try {
    // إرسال طلب للحصول على بيانات المصادقة
    const requestMessage = {
      type: 'AUTH_REQUEST',
      sourceTabId: CURRENT_TAB_ID,
      tabId: sessionStorage.getItem('tabId') || CURRENT_TAB_ID,
      timestamp: Date.now()
    };
    broadcastChannel.postMessage(requestMessage);
    return true;
  } catch (error) {
    // فشل في طلب بيانات المصادقة
    return false;
  }
};

/**
 * إلغاء تسجيل معالج الرسائل الحالي دون إغلاق القناة
 * هذه الدالة تستخدم بديلاً عن إغلاق القناة لمنع فقدان الاتصال بين التبويبات
 */
export const unregisterMessageHandler = () => {
  // إلغاء تسجيل معالج الرسائل الحالي للتبويب
  currentMessageHandler = null;
  return true;
};

/**
 * إغلاق قناة البث عند الانتهاء
 * ملاحظة: هذه الدالة لا يجب استخدامها إلا عند إغلاق التبويب فعلياً
 */
export const closeAuthChannel = () => {
  // محاولة إغلاق قناة البث للتبويب
  
  // التحقق من وجود قناة نشطة
  if (!isChannelActive) {
    // القناة غير نشطة، تجاهل طلب الإغلاق
    return false;
  }
  
  if (broadcastChannel) {
    // تم تعطيل الإغلاق هنا والاكتفاء بإلغاء تسجيل المعالج
    // سيتم الإغلاق فقط عند إغلاق التبويب باستخدام مستمع beforeunload
    currentMessageHandler = null;
    return true;
  }
  // لا توجد قناة لإغلاقها في التبويب
  return false;
};

export default {
  initAuthChannel,
  broadcastAuthData,
  requestAuthData,
  closeAuthChannel,
  unregisterMessageHandler
};
